import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, setDoc, onSnapshot, collection, getDocs } from 'firebase/firestore';
import { getUserRole, ROLES } from '../utils/roles';
import { subscribeToProducts, updateProduct as updateProductInSub } from '../services/productService';

const SpaceSettingsView = () => {
    const { spaceId } = useParams();
    const navigate = useNavigate();

    // State
    const [spaces, setSpaces] = useState([]);
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Rename state
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState('');

    // Delete confirm
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Logic options
    const [logicOptions, setLogicOptions] = useState([]);



    // System space definitions — must match App.jsx
    const SYSTEM_SPACES = useMemo(() => {
        return [
            { id: 'default', name: 'AI8SAAT', icon: 'auto_awesome', color: 'indigo', isSystem: true }
        ];
    }, []);

    // Load spaces from user doc
    useEffect(() => {
        if (!auth.currentUser) return;
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const spacesData = data.spaces || data.groups;
                let allSpaces = spacesData && Array.isArray(spacesData)
                    ? spacesData.map(({ productCount, ...rest }) => rest)
                    : [];
                // Inject system spaces if not already present
                SYSTEM_SPACES.forEach(sys => {
                    if (!allSpaces.find(s => s.id === sys.id)) {
                        allSpaces.push(sys);
                    }
                });
                setSpaces(allSpaces);
            } else {
                // No user doc yet — use system spaces only
                setSpaces([...SYSTEM_SPACES]);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Load products from subcollection — ONE-SHOT read.
    // ★ COST-FIX: Settings view doesn't need live product updates. Reuses
    // subscribeToProducts (incl. migration fallback) but unsubscribes right
    // after the first snapshot, so we don't keep a persistent products listener
    // open here. (This was a duplicate of App.jsx's always-on products listener —
    // products is a large subcollection, so a duplicate persistent listener was
    // burning QUERY reads on every settings-view mount.)
    useEffect(() => {
        if (!auth.currentUser) return;
        let unsub = null;
        unsub = subscribeToProducts(auth.currentUser.uid, (prods) => {
            setProducts(prods);
            if (unsub) { unsub(); unsub = null; } // one-shot: stop after first snapshot
        });
        return () => { if (unsub) unsub(); };
    }, []);

    // Load logic options (personas) — system spaces use system_spaces collection
    useEffect(() => {
        if (!auth.currentUser) return;
        const loadLogicOptions = async () => {
            try {
                // Determine path: system spaces load from system_spaces, custom from user's collection
                const isSystemSpace = spaceId === 'default';
                const personasPath = isSystemSpace
                    ? `system_spaces/ai8saat/personas`
                    : `users/${auth.currentUser.uid}/personas`;
                const personasRef = collection(db, ...personasPath.split('/'));
                const snapshot = await getDocs(personasRef);
                const options = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setLogicOptions(options);
            } catch (err) {
                console.error('Error loading personas:', err);
            }
        };
        loadLogicOptions();
    }, [spaceId]);

    // Current space — with fallback for default
    const currentSpace = useMemo(() => {
        const found = spaces.find(s => s.id === spaceId);
        if (found) return found;
        if (spaceId === 'default') {
            return { id: 'default', name: 'AI8SAAT', icon: 'auto_awesome', color: 'indigo', isSystem: true };
        }
        return null;
    }, [spaces, spaceId]);

    // Admin check
    const userRole = getUserRole(auth.currentUser?.email);
    const isAdmin = userRole === ROLES.ADMIN;

    // Products in this space
    const spaceProducts = useMemo(() => {
        if (spaceId === 'default') {
            return products.filter(p => !p.groupId || p.groupId === 'default');
        }
        return products.filter(p => p.groupId === spaceId);
    }, [products, spaceId]);

    // Save spaces to Firestore
    const saveSpacesToCloud = async (updatedSpaces, showNotification = false) => {
        if (!auth.currentUser) return;
        setIsSaving(true);
        try {
            // Strip productCount (computed display-only) before saving to Firestore
            const cleanSpaces = updatedSpaces.map(({ productCount, ...rest }) => rest);
            const docRef = doc(db, 'users', auth.currentUser.uid);
            await setDoc(docRef, { groups: cleanSpaces }, { merge: true });
            if (showNotification) {
                setHasUnsavedChanges(false);
                setShowSaveSuccess(true);
                setTimeout(() => setShowSaveSuccess(false), 3000);
            }
        } catch (err) {
            console.error('Save Spaces Error:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Save a single product update to subcollection
    const saveProductToSub = async (productId, updates) => {
        if (!auth.currentUser) return;
        setIsSaving(true);
        try {
            await updateProductInSub(auth.currentUser.uid, productId, updates);
        } catch (err) {
            console.error('Save Product Error:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Update space settings helper
    const updateSpaceSetting = (path, value) => {
        const updatedSpaces = spaces.map(s => {
            if (s.id === spaceId) {
                const settings = s.settings || {};
                if (path.includes('.')) {
                    const [parent, child] = path.split('.');
                    return { ...s, settings: { ...settings, [parent]: { ...(settings[parent] || {}), [child]: value } } };
                }
                return { ...s, settings: { ...settings, [path]: value } };
            }
            return s;
        });
        setSpaces(updatedSpaces);
        setHasUnsavedChanges(true);
    };

    // Explicit Save All Settings
    const handleSaveSettings = async () => {
        await saveSpacesToCloud(spaces, true);

        // ★ For system spaces: also save defaultLogicId to system_spaces collection
        // This ensures ALL users inherit admin's configured default AI logic
        if (currentSpace?.isSystem && isAdmin) {
            try {
                const systemSpaceDocId = spaceId === 'default' ? 'ai8saat' : spaceId;
                const systemSpaceRef = doc(db, 'system_spaces', systemSpaceDocId);
                const defaultLogicId = currentSpace?.settings?.defaultLogicId || 'default';
                await setDoc(systemSpaceRef, { defaultLogicId }, { merge: true });
                console.log(`[SpaceSettings] ★ Synced defaultLogicId="${defaultLogicId}" to system_spaces/${systemSpaceDocId}`);
            } catch (err) {
                console.error('[SpaceSettings] Failed to sync system space settings:', err);
            }
        }
    };

    // Update product
    const updateProduct = (productId, updates) => {
        const updatedProducts = products.map(p => p.id === productId ? { ...p, ...updates } : p);
        setProducts(updatedProducts);
        saveProductToSub(productId, updates);
    };

    // Handle Rename
    const handleRename = async () => {
        if (!renameValue.trim() || !currentSpace) return;
        const updatedSpaces = spaces.map(s =>
            s.id === spaceId ? { ...s, name: renameValue.trim() } : s
        );
        setSpaces(updatedSpaces);
        await saveSpacesToCloud(updatedSpaces);
        setIsRenaming(false);
    };

    // Handle Delete
    const handleDelete = async () => {
        // Only admin can delete system spaces
        if (currentSpace?.isSystem && !isAdmin) return;
        const updatedSpaces = spaces.filter(s => s.id !== spaceId);
        await saveSpacesToCloud(updatedSpaces);
        // Move products from deleted space to default — update each in subcollection
        if (spaceProducts.length > 0 && auth.currentUser) {
            for (const p of spaceProducts) {
                await updateProductInSub(auth.currentUser.uid, p.id, { groupId: 'default' }).catch(err =>
                    console.warn(`[SpaceSettings] Failed to move product ${p.id}:`, err.message)
                );
            }
        }
        navigate('/workspace');
    };

    // Start rename
    const startRename = () => {
        setRenameValue(currentSpace?.name || '');
        setIsRenaming(true);
    };

    // Get setting value
    const getSetting = (key, defaultValue = '') => {
        return currentSpace?.settings?.[key] ?? defaultValue;
    };

    const getLabel = (key, defaultValue) => {
        return currentSpace?.settings?.labels?.[key] ?? defaultValue;
    };

    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
                <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    // Role check — clients cannot view/edit space settings
    if (!isAdmin) {
        return (
            <div className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
                {/* Header */}
                <div className="px-8 py-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(`/space/${encodeURIComponent(currentSpace?.name || 'AI8SAAT')}`)}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <span className="material-symbols-outlined text-xl">arrow_back</span>
                        </button>
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
                            <span className="material-symbols-outlined text-xl text-primary">{currentSpace?.icon || 'folder'}</span>
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-slate-900 dark:text-white">{currentSpace?.name || 'Space'}</h1>
                            <p className="text-[11px] text-slate-400">Space Settings</p>
                        </div>
                    </div>
                </div>

                {/* Locked Content */}
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                        <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600" style={{ fontVariationSettings: "'FILL' 1" }}>lock</span>
                        </div>
                        <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-3">Default System Settings</h2>
                        <p className="text-sm text-slate-400 dark:text-slate-500 leading-relaxed mb-2">
                            This section is configured as <span className="font-semibold text-slate-500 dark:text-slate-400">system default</span> and cannot be modified.
                        </p>
                        <p className="text-xs text-slate-300 dark:text-slate-600 mb-8">
                            All Space, AI Logic, and Interface configurations have been optimized for the best experience during the Beta phase.
                        </p>
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
                            <span className="material-symbols-outlined text-sm text-slate-400">info</span>
                            <span className="text-[11px] font-medium text-slate-400">Contact Admin for any changes</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!currentSpace) {
        return (
            <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 text-center">
                <span className="material-symbols-outlined text-6xl text-slate-300 mb-4">error</span>
                <h2 className="text-xl font-bold text-slate-600 dark:text-slate-300 mb-2">Space Not Found</h2>
                <p className="text-sm text-slate-400 mb-6">The space you're looking for doesn't exist.</p>
                <button
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold text-sm hover:brightness-110 transition-all shadow-lg shadow-primary/20"
                >
                    Back to Home
                </button>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
            {/* ══════════════ HEADER ══════════════ */}
            <div className="px-8 py-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(`/space/${encodeURIComponent(currentSpace.name)}`)}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <span className="material-symbols-outlined text-xl">arrow_back</span>
                        </button>
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
                            <span className="material-symbols-outlined text-xl text-primary">{currentSpace.icon || 'folder'}</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-lg font-bold text-slate-900 dark:text-white">{currentSpace.name}</h1>
                                {currentSpace.isSystem && (
                                    <span className="text-[8px] font-bold px-1.5 py-0.5 bg-primary/10 text-primary rounded-full uppercase tracking-wider">System</span>
                                )}
                            </div>
                            <p className="text-[11px] text-slate-400">Space Settings</p>
                        </div>
                    </div>


                    {isSaving && (
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                            Saving...
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════════ CONTENT ══════════════ */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">

                {/* ════════ SETTINGS ════════ */}
                <div className="max-w-5xl mx-auto px-8 py-8 space-y-8">

                    {/* ── Row 1: Rename, Default AI Logic & Delete ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Rename Card */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-lg text-blue-500">edit</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">Rename Space</h3>
                                    <p className="text-[10px] text-slate-400">Tukar nama space ini</p>
                                </div>
                            </div>
                            {isRenaming ? (
                                <div className="space-y-3">
                                    <input
                                        type="text" value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm p-3 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        placeholder="Enter new name..." autoFocus
                                        onKeyPress={(e) => e.key === 'Enter' && handleRename()}
                                    />
                                    <div className="flex gap-2">
                                        <button onClick={handleRename} className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-bold hover:brightness-110 transition-all shadow-sm shadow-primary/20">Save</button>
                                        <button onClick={() => setIsRenaming(false)} className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 py-2.5 rounded-xl text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{currentSpace.name}</p>
                                    </div>
                                    <button onClick={startRename} disabled={currentSpace.isSystem && !isAdmin}
                                        className={`ml-3 px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${currentSpace.isSystem && !isAdmin ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40'}`}
                                    >
                                        <span className="material-symbols-outlined text-sm">edit</span> Rename
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Default AI Logic Card */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-lg text-violet-500">psychology</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">Default AI Logic</h3>
                                    <p className="text-[10px] text-slate-400">Persona AI yang akan digunakan secara default untuk space ini</p>
                                </div>
                            </div>
                            {currentSpace?.isSystem && !isAdmin ? (
                                /* Non-admin: show locked display */
                                <>
                                    <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <span className="material-symbols-outlined text-sm text-violet-500">lock</span>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                            {logicOptions.find(o => o.id === getSetting('defaultLogicId', ''))?.name || 'System Default'}
                                        </p>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[12px]">info</span>
                                        Hanya admin boleh menukar AI Logic untuk system space
                                    </p>
                                </>
                            ) : (
                                /* Admin or non-system space: editable */
                                <select
                                    value={getSetting('defaultLogicId', '')}
                                    onChange={(e) => updateSpaceSetting('defaultLogicId', e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-sm p-3 focus:ring-primary focus:border-primary"
                                >
                                    <option value="">Select a default persona...</option>
                                    {logicOptions.map(opt => (
                                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Delete Card — only admin can see for system space */}
                        {(isAdmin || !currentSpace.isSystem) && (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-lg text-rose-500">delete</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm text-slate-900 dark:text-white">Delete Space</h3>
                                        <p className="text-[10px] text-slate-400">
                                            {currentSpace.isSystem
                                                ? 'Padam system space ini — semua user turut terkesan'
                                                : 'Padam space ini (produk dipindah ke Default)'
                                            }
                                        </p>
                                    </div>
                                </div>
                                {showDeleteConfirm ? (
                                    <div className="space-y-3">
                                        <div className={`px-4 py-3 border rounded-xl ${currentSpace.isSystem ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800' : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800'}`}>
                                            <p className={`text-xs font-medium ${currentSpace.isSystem ? 'text-red-600 dark:text-red-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                {currentSpace.isSystem
                                                    ? '🚨 AMARAN: Ini adalah SYSTEM SPACE. Jika dipadam, semua user yang menggunakan space ini turut terkesan!'
                                                    : `⚠️ Adakah anda pasti? ${spaceProducts.length > 0 ? `${spaceProducts.length} produk akan dipindahkan ke space Default.` : 'Space ini akan dipadam.'}`
                                                }
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={handleDelete} className={`flex-1 text-white py-2.5 rounded-xl text-xs font-bold transition-colors shadow-sm ${currentSpace.isSystem ? 'bg-red-600 hover:bg-red-700' : 'bg-rose-500 hover:bg-rose-600'}`}>
                                                {currentSpace.isSystem ? 'Yes, Delete for ALL Users' : 'Yes, Delete'}
                                            </button>
                                            <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 py-2.5 rounded-xl text-xs font-bold">Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button onClick={() => setShowDeleteConfirm(true)}
                                        className="px-4 py-2.5 bg-rose-50 dark:bg-rose-900/20 text-rose-600 rounded-xl text-xs font-bold hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors flex items-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-sm">delete</span> Delete this Space
                                    </button>
                                )}
                            </div>
                        )}
                        {/* Non-admin: show locked placeholder for delete card */}
                        {!isAdmin && currentSpace.isSystem && (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-lg text-slate-400">lock</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm text-slate-900 dark:text-white">Delete Space</h3>
                                        <p className="text-[10px] text-slate-400">Padam space ini</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <span className="material-symbols-outlined text-sm text-slate-400">shield</span>
                                    <p className="text-xs text-slate-400">Hanya admin boleh memadam system space</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Live Product Card Preview ── */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-lg text-white">preview</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">Live Preview</h3>
                                    <p className="text-[10px] text-slate-400">Preview product card akan berubah secara real-time mengikut settings di bawah</p>
                                </div>
                            </div>
                            <span className="text-[8px] font-bold px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full uppercase tracking-wider flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                LIVE
                            </span>
                        </div>

                        {/* Mini product card preview */}
                        <div className="p-5 bg-slate-50/50 dark:bg-slate-950/50">
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-lg">
                                {/* Set tabs */}
                                <div className="flex border-b border-slate-100 dark:border-slate-800">
                                    {/* Set 1 - always visible */}
                                    <div className="flex-1 px-4 py-3 border-r border-slate-100 dark:border-slate-800 bg-primary/5">
                                        <p className="text-[10px] font-black text-primary uppercase tracking-wider text-center">
                                            {getLabel('set1', 'Set 1')}
                                        </p>
                                    </div>
                                    {/* Set 2 */}
                                    {getSetting('showSet2', true) !== false ? (
                                        <div className="flex-1 px-4 py-3 border-r border-slate-100 dark:border-slate-800">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider text-center">
                                                {getLabel('set2', 'Set 2')}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex-1 px-4 py-3 border-r border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                            <p className="text-[10px] font-bold text-slate-300 dark:text-slate-600 uppercase line-through text-center">
                                                {getLabel('set2', 'Set 2')}
                                            </p>
                                        </div>
                                    )}
                                    {/* Set 3 */}
                                    {getSetting('showSet3', true) !== false ? (
                                        <div className="flex-1 px-4 py-3">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider text-center">
                                                {getLabel('set3', 'Set 3')}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-800/50">
                                            <p className="text-[10px] font-bold text-slate-300 dark:text-slate-600 uppercase line-through text-center">
                                                {getLabel('set3', 'Set 3')}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Preview fields grid */}
                                {/* Telefon: 3 set bertindan menegak — 3 lajur pada 375px
                                    menjadikan setiap medan pratonton terlalu sempit untuk dibaca. */}
                                <div className="grid grid-cols-1 md:grid-cols-3">
                                    {/* Set 1 fields */}
                                    <div className="p-4 space-y-3 border-b md:border-b-0 md:border-r border-slate-100 dark:border-slate-800">
                                        <PreviewField label={getLabel('imagePrompt', 'Image Prompt')} color="blue" />
                                        <PreviewField label={getLabel('videoPrompt', 'Video Prompt')} color="purple" />
                                        {getSetting('showTikTokCaption', true) !== false && (
                                            <PreviewField label={getLabel('captionTiktok', 'TikTok Caption')} color="rose" />
                                        )}
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            <span className="text-[8px] font-bold text-slate-300 uppercase">Video</span>
                                        </div>
                                    </div>

                                    {/* Set 2 fields */}
                                    <div className={`p-4 space-y-3 border-b md:border-b-0 md:border-r border-slate-100 dark:border-slate-800 ${getSetting("showSet2", true) === false ? 'opacity-20' : ''}`}>
                                        {getSetting('showSet2', true) !== false ? (
                                            <>
                                                <PreviewField label={getLabel('imagePrompt', 'Image Prompt')} color="blue" />
                                                <PreviewField label={getLabel('videoPrompt', 'Video Prompt')} color="purple" />
                                                {getSetting('showTikTokCaption', true) !== false && (
                                                    <PreviewField label={getLabel('captionTiktok', 'TikTok Caption')} color="rose" />
                                                )}
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                    <span className="text-[8px] font-bold text-slate-300 uppercase">Video</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="h-full flex items-center justify-center">
                                                <span className="material-symbols-outlined text-xl text-slate-200 dark:text-slate-700">visibility_off</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Set 3 fields */}
                                    <div className={`p-4 space-y-3 ${getSetting('showSet3', true) === false ? 'opacity-20' : ''}`}>
                                        {getSetting('showSet3', true) !== false ? (
                                            <>
                                                <PreviewField label={getLabel('imagePrompt', 'Image Prompt')} color="blue" />
                                                <PreviewField label={getLabel('videoPrompt', 'Video Prompt')} color="purple" />
                                                {getSetting('showTikTokCaption', true) !== false && (
                                                    <PreviewField label={getLabel('captionTiktok', 'TikTok Caption')} color="rose" />
                                                )}
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                    <span className="text-[8px] font-bold text-slate-300 uppercase">Video</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="h-full flex items-center justify-center">
                                                <span className="material-symbols-outlined text-xl text-slate-200 dark:text-slate-700">visibility_off</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* ── Interface Customization ── */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-9 h-9 rounded-xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
                                <span className="material-symbols-outlined text-lg text-teal-500">palette</span>
                            </div>
                            <div>
                                <h3 className="font-bold text-sm text-slate-900 dark:text-white">Interface Customization</h3>
                                <p className="text-[10px] text-slate-400">Custom label untuk prompt fields</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <p className="text-[9px] font-bold text-slate-500 uppercase">Image Prompt Label</p>
                                    <input type="text"
                                        value={getLabel('imagePrompt', 'Image Prompt')}
                                        onChange={(e) => updateSpaceSetting('labels.imagePrompt', e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[9px] font-bold text-slate-500 uppercase">Video Prompt Label</p>
                                    <input type="text"
                                        value={getLabel('videoPrompt', 'Video Prompt')}
                                        onChange={(e) => updateSpaceSetting('labels.videoPrompt', e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <p className="text-[9px] font-bold text-slate-500 uppercase">TikTok Caption Label</p>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input type="checkbox"
                                            checked={getSetting('showTikTokCaption', true) !== false}
                                            onChange={(e) => updateSpaceSetting('showTikTokCaption', e.target.checked)}
                                            className="w-3 h-3 rounded border-slate-300 text-primary"
                                        />
                                        <span className="text-[9px] font-bold text-slate-400">SHOW FIELD</span>
                                    </label>
                                </div>
                                <input type="text"
                                    value={getLabel('captionTiktok', 'TikTok Caption')}
                                    onChange={(e) => updateSpaceSetting('labels.captionTiktok', e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5 disabled:opacity-50"
                                    disabled={getSetting('showTikTokCaption', true) === false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Set Management ── */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                                <span className="material-symbols-outlined text-lg text-indigo-500">layers</span>
                            </div>
                            <div>
                                <h3 className="font-bold text-sm text-slate-900 dark:text-white">Set Management</h3>
                                <p className="text-[10px] text-slate-400">Konfigurasi nama dan visibility setiap Box</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {/* Set 1 */}
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-bold text-slate-500">BOX 1 TITLE</p>
                                    <span className="text-[8px] px-1.5 py-0.5 bg-emerald-100 text-emerald-600 rounded">ALWAYS ON</span>
                                </div>
                                <input type="text"
                                    value={getLabel('set1', 'Set 1')}
                                    onChange={(e) => updateSpaceSetting('labels.set1', e.target.value)}
                                    className="w-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2"
                                />
                            </div>
                            {/* Set 2 */}
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-bold text-slate-500">BOX 2 TITLE</p>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input type="checkbox"
                                            checked={getSetting('showSet2', true) !== false}
                                            onChange={(e) => updateSpaceSetting('showSet2', e.target.checked)}
                                            className="w-3 h-3 rounded border-slate-300 text-primary"
                                        />
                                        <span className="text-[9px] font-bold text-slate-400 uppercase">Visible</span>
                                    </label>
                                </div>
                                <input type="text"
                                    value={getLabel('set2', 'Set 2')}
                                    onChange={(e) => updateSpaceSetting('labels.set2', e.target.value)}
                                    className="w-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2 disabled:opacity-50"
                                    disabled={getSetting('showSet2', true) === false}
                                />
                            </div>
                            {/* Set 3 */}
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-bold text-slate-500">BOX 3 TITLE</p>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input type="checkbox"
                                            checked={getSetting('showSet3', true) !== false}
                                            onChange={(e) => updateSpaceSetting('showSet3', e.target.checked)}
                                            className="w-3 h-3 rounded border-slate-300 text-primary"
                                        />
                                        <span className="text-[9px] font-bold text-slate-400 uppercase">Visible</span>
                                    </label>
                                </div>
                                <input type="text"
                                    value={getLabel('set3', 'Set 3')}
                                    onChange={(e) => updateSpaceSetting('labels.set3', e.target.value)}
                                    className="w-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2 disabled:opacity-50"
                                    disabled={getSetting('showSet3', true) === false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Save Button ── */}
                    <div className="flex items-center justify-center gap-3 pt-2 pb-10">
                        {hasUnsavedChanges && (
                            <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                                <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide">Unsaved changes</span>
                            </div>
                        )}
                        <button
                            onClick={handleSaveSettings}
                            disabled={isSaving}
                            className={`px-8 py-3 rounded-2xl font-bold text-xs flex items-center gap-2 transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg uppercase tracking-wide ${isSaving
                                ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed shadow-none'
                                : hasUnsavedChanges
                                    ? 'bg-primary hover:bg-indigo-600 text-white shadow-primary/30 ring-2 ring-primary/20'
                                    : 'bg-primary hover:bg-indigo-600 text-white shadow-primary/30'
                                }`}
                        >
                            {isSaving ? (
                                <>
                                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-sm">save</span>
                                    Save Settings
                                </>
                            )}
                        </button>
                    </div>

                </div>
            </div>

            {/* ══════════════ SUCCESS TOAST ══════════════ */}
            <div className={`fixed top-6 right-6 z-[100] transition-all duration-500 ease-out ${showSaveSuccess
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 -translate-y-4 scale-95 pointer-events-none'
                }`}>
                <div className="flex items-center gap-3 bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl px-5 py-3.5 shadow-2xl shadow-emerald-500/10">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <span className="material-symbols-outlined text-lg text-emerald-500">check_circle</span>
                    </div>
                    <div>
                        <p className="text-sm font-bold text-slate-800 dark:text-white">Settings Saved!</p>
                        <p className="text-[10px] text-slate-400">All changes have been saved successfully.</p>
                    </div>
                    <button
                        onClick={() => setShowSaveSuccess(false)}
                        className="ml-2 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-slate-600"
                    >
                        <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ══════════════ Sub-components ══════════════ */

const PromptField = ({ label, color, value }) => {
    const dotColors = {
        blue: 'bg-blue-500',
        purple: 'bg-purple-500',
        rose: 'bg-rose-500',
    };

    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${dotColors[color] || 'bg-slate-400'}`}></span>
                    {label}
                </label>
                {value && (
                    <button onClick={() => navigator.clipboard.writeText(value)}
                        className="text-[10px] font-bold text-slate-300 hover:text-primary transition-colors flex items-center gap-0.5 uppercase">
                        COPY <span className="material-symbols-outlined text-[14px]">content_copy</span>
                    </button>
                )}
            </div>
            <div className="w-full min-h-[100px] bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-100 dark:border-slate-800 text-xs leading-relaxed text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                {value || <span className="text-slate-300 dark:text-slate-600 italic">No content</span>}
            </div>
        </div>
    );
};

const VideoPreview = ({ video }) => {
    return (
        <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Video
            </label>
            {video ? (
                <div className="rounded-xl overflow-hidden border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/20">
                    <div onClick={() => alert("Preview video telah dipadam dari Webapp untuk menjimatkan kos dan kuota. Sila lihat preview di panel Extension.")} className="w-full flex flex-col items-center justify-center p-6 bg-slate-900 cursor-pointer group hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined text-[32px] text-emerald-400 group-hover:scale-110 transition-transform mb-2">extension</span>
                        <span className="text-[10px] font-bold text-slate-400 text-center uppercase tracking-wide">Preview in Extension</span>
                    </div>
                    <div className="px-3 py-1.5 flex items-center justify-between">
                        <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-medium truncate max-w-[200px]">{video.substring(0, 60)}...</span>
                        <button onClick={() => navigator.clipboard.writeText(video)} className="text-[9px] text-emerald-500 hover:text-emerald-700 font-bold">COPY</button>
                    </div>
                </div>
            ) : (
                <div className="h-[40px] bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center">
                    <span className="text-[10px] text-slate-400">No video uploaded</span>
                </div>
            )}
        </div>
    );
};

const PreviewField = ({ label, color }) => {
    const dotColors = {
        blue: 'bg-blue-500',
        purple: 'bg-purple-500',
        rose: 'bg-rose-500',
    };

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${dotColors[color] || 'bg-slate-400'}`}></span>
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
            </div>
            <div className="h-6 bg-slate-100 dark:bg-slate-800 rounded-md w-full"></div>
        </div>
    );
};

export default SpaceSettingsView;
